import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  TextContent,
  Badge,
  Alert,
  StatusIndicator,
  ProgressBar,
  ExpandableSection,
  Spinner
} from '@cloudscape-design/components';
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble";
import Avatar from "@cloudscape-design/chat-components/avatar";

// Enhanced markdown renderer for insights content
const renderMarkdown = (text) => {
  if (!text) return text;

  // Pre-process text to handle various formatting
  let processedText = text
    // Handle headers
    .replace(/^### (.*$)/gm, '<h3 style="margin: 16px 0 8px 0; font-weight: 600; font-size: 1.1em;">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 style="margin: 20px 0 10px 0; font-weight: 600; font-size: 1.2em;">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 style="margin: 24px 0 12px 0; font-weight: 600; font-size: 1.3em;">$1</h1>')
    // Handle code blocks
    .replace(/```([\s\S]*?)```/g, '<pre style="padding: 12px; border-radius: 4px; margin: 12px 0; overflow-x: auto; font-family: monospace; font-size: 0.9em; opacity: 0.9;"><code>$1</code></pre>')
    // Handle inline code
    .replace(/`([^`]+)`/g, '<code style="padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em; opacity: 0.85;">$1</code>')
    // Handle blockquotes
    .replace(/^> (.*$)/gm, '<blockquote style="border-left: 4px solid currentColor; margin: 12px 0; padding: 8px 16px; font-style: italic; opacity: 0.85;">$1</blockquote>')
    // Handle horizontal rules
    .replace(/^---+$/gm, '<hr style="margin: 20px 0; border: none; border-top: 1px solid currentColor; opacity: 0.2;" />')
    // Handle links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="text-decoration: none; font-weight: 500;" target="_blank" rel="noopener noreferrer">$1</a>');

  // Split into paragraphs
  const paragraphs = processedText.split('\n\n');

  return paragraphs.map((paragraph, pIndex) => {
    // Skip if paragraph is already processed HTML
    if (paragraph.includes('<h1>') || paragraph.includes('<h2>') || paragraph.includes('<h3>') ||
      paragraph.includes('<pre>') || paragraph.includes('<blockquote>') || paragraph.includes('<hr')) {
      return <div key={pIndex} dangerouslySetInnerHTML={{ __html: paragraph }} />;
    }

    // Handle numbered lists
    if (paragraph.match(/^\d+\./m)) {
      const listItems = paragraph.split(/(?=\d+\.)/);
      return (
        <ol key={pIndex} style={{ marginBottom: '16px', paddingLeft: '20px', lineHeight: '1.6' }}>
          {listItems.filter(item => item.trim()).map((item, iIndex) => {
            const cleanItem = item.replace(/^\d+\.\s*/, '').trim();
            return (
              <li key={iIndex} style={{ marginBottom: '8px' }}>
                <span dangerouslySetInnerHTML={{
                  __html: cleanItem
                    .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                    .replace(/`([^`]+)`/g, '<code style="padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em; opacity: 0.85;">$1</code>')
                }} />
              </li>
            );
          })}
        </ol>
      );
    }

    // Handle bullet lists (including various bullet characters)
    if (paragraph.match(/^[-*•]/m)) {
      const listItems = paragraph.split('\n').filter(line => line.match(/^[-*•]/));
      return (
        <ul key={pIndex} style={{ marginBottom: '16px', paddingLeft: '20px', lineHeight: '1.6' }}>
          {listItems.map((item, iIndex) => {
            const cleanItem = item.replace(/^[-*•]\s*/, '').trim();
            return (
              <li key={iIndex} style={{ marginBottom: '6px' }}>
                <span dangerouslySetInnerHTML={{
                  __html: cleanItem
                    .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                    .replace(/`([^`]+)`/g, '<code style="padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em; opacity: 0.85;">$1</code>')
                }} />
              </li>
            );
          })}
        </ul>
      );
    }

    // Handle regular paragraphs with enhanced formatting
    if (paragraph.trim()) {
      return (
        <p key={pIndex} style={{ marginBottom: '14px', lineHeight: '1.6' }}>
          <span dangerouslySetInnerHTML={{
            __html: paragraph
              .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
              .replace(/`([^`]+)`/g, '<code style="padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 0.9em; opacity: 0.85;">$1</code>')
              .replace(/\n/g, '<br />')
          }} />
        </p>
      );
    }

    return null;
  }).filter(Boolean);
};

const InsightsSection = ({
  insightsState,
  insightsData,
  error,
  progress,
  currentStage,
  processingTime
}) => {

  const getInsightCategoryBadge = (category) => {
    const categoryColors = {
      'financial_performance': 'blue',
      'risk_assessment': 'red',
      'strategic_insights': 'green',
      'market_analysis': 'purple',
      'operational_efficiency': 'orange',
      'competitive_landscape': 'grey'
    };

    const color = categoryColors[category] || 'grey';
    const displayName = category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return <Badge color={color}>{displayName}</Badge>;
  };

  const getConfidenceBadge = (confidence) => {
    const percentage = Math.round(confidence * 100);
    let color, label;

    if (confidence >= 0.8) {
      color = 'green';
      label = 'High Confidence';
    } else if (confidence >= 0.6) {
      color = 'blue';
      label = 'Medium Confidence';
    } else {
      color = 'grey';
      label = 'Low Confidence';
    }

    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Badge color={color}>{label}</Badge>
        <div style={{
          width: '60px',
          height: '8px',
          backgroundColor: 'currentColor',
          opacity: 0.2,
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: color === 'green' ? '#16a085' : color === 'blue' ? '#3498db' : '#95a5a6',
            borderRadius: '4px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '12px', opacity: 0.7, minWidth: '30px' }}>{percentage}%</span>
      </SpaceBetween>
    );
  };

  if (insightsState === 'not_available') {
    return null; // Don't show anything if no session ID
  }

  if (insightsState === 'loading') {
    return (
      <Container>
        <SpaceBetween size="m">
          <Header variant="h2">
            <SpaceBetween direction="horizontal" size="s" alignItems="center">
              <Spinner size="normal" />
              AI Insights Generation
            </SpaceBetween>
          </Header>

          <Alert type="info">
            <SpaceBetween size="s">
              <div>
                <strong>Generating AI insights from your research results...</strong>
              </div>
              <div>
                Our AI agents are analyzing the research findings to identify key patterns,
                risks, and strategic insights across all questions.
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                <strong>Current Stage:</strong> {currentStage}
              </div>
              {processingTime > 0 && (
                <div style={{ fontSize: '14px', opacity: 0.7 }}>
                  <strong>Processing Time:</strong> {processingTime}s
                </div>
              )}
            </SpaceBetween>
          </Alert>

          {progress > 0 && (
            <Box>
              <ProgressBar
                value={progress}
                label="Insights Generation Progress"
                description={`${Math.round(progress)}% complete`}
              />
            </Box>
          )}
        </SpaceBetween>
      </Container>
    );
  }

  if (insightsState === 'failed') {
    return (
      <Container>
        <SpaceBetween size="m">
          <Header variant="h2">AI Insights Generation</Header>

          <Alert type="error">
            <SpaceBetween size="s">
              <div>
                <strong>Insights generation encountered an issue</strong>
              </div>
              <div>
                {error || 'An unexpected error occurred during insights generation.'}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                The research results are still available above. You can try refreshing the page
                to attempt insights generation again.
              </div>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>
    );
  }

  if (insightsState === 'completed' && insightsData) {
    // Group insights by category
    const insightsByCategory = {};
    const categoryInfo = {
      'strategic': {
        title: '🎯 Strategic Insights',
        description: 'Long-term strategic considerations and competitive positioning',
        color: 'green',
        gradient: 'linear-gradient(135deg, #16a085 0%, #138d75 100%)',
        icon: 'status-positive'
      },
      'risk': {
        title: '⚠️ Risk Assessment',
        description: 'Potential risks and mitigation strategies',
        color: 'red',
        gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
        icon: 'status-warning'
      },
      'opportunity': {
        title: '📈 Growth Opportunities',
        description: 'Market opportunities and expansion potential',
        color: 'blue',
        gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
        icon: 'status-info'
      },
      'performance': {
        title: '📊 Performance Analysis',
        description: 'Financial and operational performance insights',
        color: 'purple',
        gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
        icon: 'analytics'
      },
      'trend': {
        title: '📈 Market Trends',
        description: 'Industry trends and market dynamics',
        color: 'orange',
        gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
        icon: 'trending-up'
      },
      'compliance': {
        title: '🛡️ Compliance & ESG',
        description: 'Regulatory compliance and ESG considerations',
        color: 'grey',
        gradient: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
        icon: 'security'
      },
      'theme': {
        title: '🔍 Key Themes',
        description: 'Cross-cutting themes and patterns',
        color: 'blue',
        gradient: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
        icon: 'folder'
      },
      'contradiction': {
        title: '⚡ Contradictions',
        description: 'Conflicting information requiring attention',
        color: 'red',
        gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
        icon: 'status-negative'
      }
    };

    // Group insights by category
    if (insightsData.insights) {
      insightsData.insights.forEach(insight => {
        const category = insight.category || 'theme';
        if (!insightsByCategory[category]) {
          insightsByCategory[category] = [];
        }
        insightsByCategory[category].push(insight);
      });
    }

    // Sort categories by priority (strategic first, then by count)
    const categoryOrder = ['strategic', 'opportunity', 'risk', 'performance', 'trend', 'compliance', 'theme', 'contradiction'];
    const sortedCategories = Object.keys(insightsByCategory).sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a);
      const bIndex = categoryOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return insightsByCategory[b].length - insightsByCategory[a].length;
    });

    return (
      <Container>
        <SpaceBetween size="l">
          <Header
            variant="h2"
            description={`AI-powered analysis completed in ${Math.round(insightsData.processing_time || 0)}s`}
          >
            <img 
              src="/images/logo/researchAI_Logo.png" 
              alt="ResearchAI" 
              style={{ 
                width: '24px', 
                height: '24px', 
                marginRight: '8px',
                verticalAlign: 'middle'
              }}
            />
            AI-Generated Investment Insights
          </Header>

          {/* Key Metrics Dashboard */}
          <Container>
            <SpaceBetween direction="horizontal" size="l">
              <Box padding="m" variant="div" style={{
                background: 'linear-gradient(135deg, #0073bb 0%, #005a9f 100%)',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                minWidth: '140px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {insightsData.insights?.length || 0}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  Total Insights
                </div>
              </Box>

              <Box padding="m" variant="div" style={{
                background: 'linear-gradient(135deg, #16a085 0%, #138d75 100%)',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                minWidth: '140px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {Object.keys(insightsByCategory).length}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  Categories
                </div>
              </Box>

              <Box padding="m" variant="div" style={{
                background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                minWidth: '140px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {Math.round(insightsData.processing_time || 0)}s
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  Processing Time
                </div>
              </Box>

              <Box padding="m" variant="div" style={{
                background: 'linear-gradient(135deg, #8e44ad 0%, #7d3c98 100%)',
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                minWidth: '140px'
              }}>
                {/* <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {insightsData.insights ? Math.round((insightsData.insights.filter(i => i.confidence_level >= 0.8).length / insightsData.insights.length) * 100) : 0}%
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  High Confidence
                </div> */}
              </Box>
            </SpaceBetween>
          </Container>

          {/* <Alert type="success" dismissible>
            <SpaceBetween size="s">
              <div>
                <strong>🎯 AI Analysis Complete!</strong> Generated {insightsData.insights?.length || 0} investment-grade insights in {Math.round(insightsData.processing_time || 0)} seconds
              </div>
              <div>
                Our AI agents analyzed your research results using advanced pattern recognition to identify strategic opportunities, risks, and key investment considerations.
              </div>
            </SpaceBetween>
          </Alert> */}

          {insightsData.summary && (
            <Container>
              <Header variant="h3">Executive Summary</Header>
              <ChatBubble
                ariaLabel="AI-generated executive summary"
                type="outgoing"
                avatar={
                  <Avatar
                    color="gen-ai"
                    iconName="gen-ai"
                    ariaLabel="Insights AI"
                    tooltipText="Insights AI"
                  />
                }
              >
                <div style={{ lineHeight: '1.6' }}>
                  {renderMarkdown(insightsData.summary)}
                </div>
              </ChatBubble>
            </Container>
          )}

          <SpaceBetween size="l">
            {sortedCategories.map((category, categoryIndex) => {
              const categoryInsights = insightsByCategory[category];
              const categoryMeta = categoryInfo[category] || categoryInfo['theme'];

              return (
                <div key={category} style={{
                  background: 'transparent',
                  borderRadius: '12px',
                  padding: '4px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    borderRadius: '8px',
                    padding: '0px'
                  }}>
                    <ExpandableSection
                      headerText={
                        <SpaceBetween direction="horizontal" size="s" alignItems="center">
                          <span style={{ fontSize: '18px', fontWeight: '600' }}>{categoryMeta.title}</span>
                          <Badge color={categoryMeta.color}>
                            {categoryInsights.length} insight{categoryInsights.length !== 1 ? 's' : ''}
                          </Badge>
                        </SpaceBetween>
                      }
                      headerDescription={categoryMeta.description}
                      defaultExpanded={categoryIndex === 0}
                    >
                      <SpaceBetween size="m">
                        {categoryInsights.map((insight, insightIndex) => (
                          <Container key={insightIndex}>
                            <Header
                              variant="h4"
                              actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                  {insight.confidence_level && getConfidenceBadge(insight.confidence_level)}
                                </SpaceBetween>
                              }
                            >
                              💡 {insight.title}
                            </Header>

                            <ChatBubble
                              ariaLabel={`AI insight: ${insight.title}`}
                              type="outgoing"
                              avatar={
                                <Avatar
                                  color="gen-ai"
                                  iconName="gen-ai"
                                  ariaLabel="Insights AI"
                                  tooltipText="Insights AI"
                                />
                              }
                            >
                              <SpaceBetween size="m">
                                <div style={{ lineHeight: '1.6' }}>
                                  {renderMarkdown(insight.description)}
                                </div>

                                {insight.supporting_evidence && insight.supporting_evidence.length > 0 && (
                                  <Box>
                                    <TextContent>
                                      <h4>Supporting Evidence:</h4>
                                      <ul style={{ paddingLeft: '20px' }}>
                                        {insight.supporting_evidence.map((evidence, evidenceIndex) => (
                                          <li key={evidenceIndex} style={{ marginBottom: '4px' }}>
                                            {evidence}
                                          </li>
                                        ))}
                                      </ul>
                                    </TextContent>
                                  </Box>
                                )}

                                {insight.recommendations && insight.recommendations.length > 0 && (
                                  <Box>
                                    <TextContent>
                                      <h4>Recommendations:</h4>
                                      <ul style={{ paddingLeft: '20px' }}>
                                        {insight.recommendations.map((recommendation, recIndex) => (
                                          <li key={recIndex} style={{ marginBottom: '4px' }}>
                                            {recommendation}
                                          </li>
                                        ))}
                                      </ul>
                                    </TextContent>
                                  </Box>
                                )}


                                {insight.related_questions && insight.related_questions.length > 0 && (
                                  <Box>
                                    <TextContent>
                                      <h4>🔗 Related Questions:</h4>
                                      <div style={{ fontSize: '14px', opacity: 0.7 }}>
                                        {insight.related_questions.join(', ')}
                                      </div>
                                    </TextContent>
                                  </Box>
                                )}
                              </SpaceBetween>
                            </ChatBubble>
                          </Container>
                        ))}
                      </SpaceBetween>
                    </ExpandableSection>
                  </div>
                </div>
              );
            })}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    );
  }

  return null;
};

export default InsightsSection;